import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessAndPeopleApi } from '@apply/api/src/api/BusinessAndPeopleApi';
import { applicationInsights } from '../../ApplicationInsights';
import { alertModal } from '@apply/ui';
import { useAuthentication } from '../../LoginPage';
import { FeatureType } from '@apply/constants';
import { IDashboardApplicationItem } from '@apply/api/src/api/ApplyApi';
import { Button, ButtonTheme, Tab, TabKeyIdentifierPrefix, TabPanel, TabPanelTheme } from '@apply/components';
import { ApplyDraftPanel } from './ApplyDraftPanel';
import { ApplyPendingApprovalPanel } from './ApplyPendingApprovalPanel';
import { LoansApprovedToClosePanel } from './LoansApprovedToClosePanel';
import { LoanDrawPanel } from './LoanDrawPanel/LoanDrawPanel';
import { updateApiId } from '@apply/store/src/borrower/actions';
import { useDispatch } from 'react-redux';
import { IDashboardDrawBudget } from '@apply/api/src/api/DrawApi';

interface IDashboardParameters {
    inProgressApplications: IDashboardApplicationItem[];
    applicationsPendingApproval: IDashboardApplicationItem[];
    applicationsReadyToClose: IDashboardApplicationItem[];
    deleteApplication: (applicationId: number) => void;
    dashboardDrawBudgets: IDashboardDrawBudget[];
    loading: boolean;
}

export const MobileDashboard: React.FC<IDashboardParameters> = ({
    inProgressApplications,
    applicationsPendingApproval,
    applicationsReadyToClose,
    deleteApplication,
    dashboardDrawBudgets,
    loading,
}) => {
    const dispatch = useDispatch();
    const businessAndPeopleApi = new BusinessAndPeopleApi(applicationInsights);
    const navigate = useNavigate();
    const { isFeatureEnabled } = useAuthentication();
    
    const loadUserBusinesses = async () => {
        try {
            const responseBusinesses = await businessAndPeopleApi.getBorrowerBusinesses();
            if (responseBusinesses.statusCode !== 200) {
                alertModal({
                    message: 'Failed to load Borrower Business Information. Please try again.',
                });
            }

            const result = responseBusinesses.result;
            if (result && result.length == 0) {
                navigate(`/business?redirectTo=/borrower`);
            } else {
                navigate('/borrower');
            }
        } catch {
            alertModal({
                message: 'Failed to load Borrower Entities. Please try again.',
            });
        }
    };

    const onNewApplication = () => {
        // BFIA-4083 Wipe the apiId so we do not accidently reuse the same appliation. We want a new one.
        dispatch(updateApiId(''));

        //Check if feature flag is enabled
        if (!isFeatureEnabled(FeatureType.BPHierarchyPhase1)) {
            navigate('/borrower');
        } else {
            //Check to see if a top level business exists
            loadUserBusinesses();
        }
    };

    return (
        <div className='content'>
            <div className='contentSection'>
                <TabPanel
                    id='mobileLandingTabPanel'
                    theme={TabPanelTheme.Blue}
                    defaultIndex={0}
                    width='100%'
                >
                    <Tab
                        id='mobileLandingApplyTab'
                        text='Apply'
                        key={`${TabKeyIdentifierPrefix}Apply`}
                    >
                        <div style={{ marginLeft: '10%', marginRight: '10%' }}>
                            <Button
                                id='homeNewApplicationButton'
                                theme={ButtonTheme.Primary}
                                width='100%'
                                handleClick={onNewApplication}
                            >
                                New Application
                            </Button>
                        </div>
                        <ApplyDraftPanel
                            applications={inProgressApplications}
                            deleteApplication={deleteApplication}
                        />
                        <ApplyPendingApprovalPanel applications={applicationsPendingApproval} />
                    </Tab>
                    <Tab
                        id='mobileLandingLoanTab'
                        text='Loans'
                        key={`${TabKeyIdentifierPrefix}Loans`}
                    >
                        <LoansApprovedToClosePanel applications={applicationsReadyToClose} />
                        <LoanDrawPanel dashboardDrawBudgets={dashboardDrawBudgets} loading={loading} />
                    </Tab>
                </TabPanel>
            </div>
        </div>
    );
};
